import { cn } from "cn";
import { useEffect, useState } from "react";
import { AttachmentPlayMark } from "./attachment-play-mark";
import {
  type AttachmentFile,
  resolveAttachmentPreviewKind,
} from "./attachment-preview-kind";
import { AttachmentTypeIcon } from "./attachment-type-icon";

/**
 * A file's small square picture — 40px unless `className` sizes it: an image or
 * a video's poster (with a play mark), cropped to fill — or, when there is none
 * to paint (a document, an audio note, an image the browser cannot decode), the
 * file's type icon at full size. The list row's picture, and the file stacks of
 * the share and delete dialogs.
 */
export function AttachmentPicture({
  file,
  className,
}: {
  file: AttachmentFile;
  className?: string;
}) {
  const preview = resolveAttachmentPreviewKind(file);
  const src = preview.kind === "picture" ? preview.src : null;

  // A new picture deserves a fresh attempt after an earlier one failed.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    // `inline-block` sits on the text baseline exactly as the bare <img> it wraps did.
    return (
      <span
        className={cn(
          /* sorted */
          "relative inline-block overflow-hidden",
          "size-10",
          "bg-bg-dark-min rounded-lg",
          className,
        )}
      >
        <img
          src={src}
          alt={file.fileName}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed(true)}
          className="block object-cover size-full"
        />
        {preview.kind === "picture" && preview.isVideo && (
          <AttachmentPlayMark />
        )}
      </span>
    );
  }

  return (
    <AttachmentTypeIcon
      fileName={file.fileName}
      contentType={file.contentType}
      size={40}
      className={cn("block", className)}
    />
  );
}
